import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';

import { type AuditLogFilters, type AuditLogRow, auditExportCsvUrl, fetchAuditLog } from '@/lib/adminApi';

interface FilterState {
  actor: string;
  action: string;
  target: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = { actor: '', action: '', target: '', from: '', to: '' };

function toQuery(filters: FilterState, cursor: string | null): AuditLogFilters {
  return {
    actor: filters.actor || undefined,
    action: filters.action || undefined,
    target: filters.target || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    cursor: cursor ?? undefined,
  };
}

/**
 * GET /api/admin/v2/audit + export.csv (support-admin rebuild Phase 1,
 * ClickUp 869f084w5 / 869f0ev36). Every admin role reads the whole log
 * (app/api/admin_audit_viewer.py's own role decision, mirrored here — no
 * client-side role gate on top of a route that already admits everyone).
 * `action` renders whatever string a write endpoint chose — no local enum,
 * matching the backend's "no enum lock-in".
 */
export function AuditLogPage() {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const cursor = cursorStack[cursorStack.length - 1];

  const { data, isPending, isError } = useQuery({
    queryKey: ['admin-audit', filters, cursor],
    queryFn: () => fetchAuditLog(toQuery(filters, cursor)),
  });

  function updateFilter<K extends keyof FilterState>(key: K, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCursorStack([null]);
  }

  function goNext() {
    if (data?.next_cursor) setCursorStack((stack) => [...stack, data.next_cursor as string]);
  }
  function goPrev() {
    setCursorStack((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack));
  }

  const rows = data?.rows ?? [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-h2 font-semibold text-fg-primary">Audit log</h1>
        <a
          href={auditExportCsvUrl(toQuery(filters, null))}
          className="inline-flex items-center gap-2 rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          Export CSV
        </a>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <input
          aria-label="Actor"
          placeholder="Actor email"
          value={filters.actor}
          onChange={(event) => updateFilter('actor', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <input
          aria-label="Action"
          placeholder="Action"
          value={filters.action}
          onChange={(event) => updateFilter('action', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <input
          aria-label="Target"
          placeholder="Target id"
          value={filters.target}
          onChange={(event) => updateFilter('target', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <input
          aria-label="From"
          type="date"
          value={filters.from}
          onChange={(event) => updateFilter('from', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
        <input
          aria-label="To"
          type="date"
          value={filters.to}
          onChange={(event) => updateFilter('to', event.target.value)}
          className="rounded-card border border-border bg-surface-field px-3 py-1.5 text-body text-fg-primary"
        />
      </div>

      {isError && (
        <p
          role="alert"
          className="mt-4 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
        >
          Could not load the audit log.
        </p>
      )}

      {!isPending && !isError && rows.length === 0 && (
        <p className="mt-8 text-body text-fg-tertiary">No actions match these filters.</p>
      )}

      {rows.length > 0 && (
        <table className="mt-4 w-full text-left text-body">
          <thead>
            <tr className="border-b border-border text-meta text-fg-tertiary">
              <th className="py-2 pr-4 font-medium">Time</th>
              <th className="py-2 pr-4 font-medium">Actor</th>
              <th className="py-2 pr-4 font-medium">Action</th>
              <th className="py-2 pr-4 font-medium">Target</th>
              <th className="py-2 pr-4 font-medium">Reason</th>
              <th className="py-2 font-medium">Payload</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <AuditRow
                key={row.id}
                row={row}
                expanded={expandedId === row.id}
                onToggle={() => setExpandedId((id) => (id === row.id ? null : row.id))}
              />
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          disabled={cursorStack.length <= 1}
          onClick={goPrev}
          className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!data?.next_cursor}
          onClick={goNext}
          className="rounded-card border border-border px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function AuditRow({
  row,
  expanded,
  onToggle,
}: {
  row: AuditLogRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="border-b border-border">
        <td className="py-2 pr-4 text-fg-secondary">{new Date(row.created_at).toLocaleString()}</td>
        <td className="py-2 pr-4 text-fg-primary">{row.actor_email}</td>
        <td className="py-2 pr-4 text-fg-primary">{row.action}</td>
        <td className="py-2 pr-4 text-fg-secondary">
          {row.target_kind}:{row.target_id ?? '—'}
        </td>
        <td className="py-2 pr-4 text-fg-secondary">{row.reason}</td>
        <td className="py-2">
          <button
            type="button"
            onClick={onToggle}
            className="text-meta font-medium text-fg-secondary underline hover:text-fg-primary"
          >
            {expanded ? 'Hide' : 'View'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-surface-sunken px-4 py-3">
            <pre className="whitespace-pre-wrap break-all font-mono text-micro text-fg-secondary">
              {JSON.stringify(row.payload, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}
